import { supabase } from '@/integrations/supabase/client';

export interface TokenRecord {
  id: string;
  user_id: string | null;
  email: string;
  token_type: string;
  token: string;
  payload: any;
  expires_at: string;
  used_at: string | null;
  created_at: string;
  updated_at: string;
}

export type TokenType = 'password_reset' | 'email_verification' | 'magic_link' | 'api_key';

export interface CreateTokenParams {
  email: string;
  tokenType: TokenType;
  payload?: any;
  expiryHours?: number;
}

export class TokenService {
  private static DEFAULT_EXPIRY_HOURS: Record<TokenType, number> = {
    password_reset: 1,
    email_verification: 24,
    magic_link: 1,
    api_key: 720
  };

  private static TOKEN_LENGTH = 64;

  private static generateToken(): string {
    const array = new Uint8Array(this.TOKEN_LENGTH);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  }

  static async createToken(params: CreateTokenParams): Promise<{
    success: boolean;
    token?: string;
    error?: string;
  }> {
    try {
      const { email, tokenType, payload = {}, expiryHours } = params;
      
      let userId: string | null = null;
      
      const { data: { user } } = await supabase.auth.getUser();
      if (user?.email === email) {
        userId = user.id;
      }

      const token = this.generateToken();
      
      const expiresAt = new Date();
      const hours = expiryHours || this.DEFAULT_EXPIRY_HOURS[tokenType] || 1;
      expiresAt.setHours(expiresAt.getHours() + hours);

      await supabase
        .from('tokens')
        .delete()
        .eq('email', email)
        .eq('token_type', tokenType);

      const { error: insertError } = await supabase
        .from('tokens')
        .insert({
          user_id: userId,
          email,
          token_type: tokenType,
          token,
          payload,
          expires_at: expiresAt.toISOString(),
          used_at: null
        });

      if (insertError) {
        return { success: false, error: `Failed to create ${tokenType} token` };
      }

      return { success: true, token };
    } catch (error) {
      return { success: false, error: 'An unexpected error occurred' };
    }
  }

  static async validateToken(token: string, tokenType?: TokenType): Promise<{
    valid: boolean;
    record?: TokenRecord;
    error?: string;
  }> {
    try {
      let query = supabase
        .from('tokens')
        .select('*')
        .eq('token', token)
        .is('used_at', null);

      if (tokenType) {
        query = query.eq('token_type', tokenType);
      }

      const { data, error } = await query.maybeSingle();

      if (error) {
        return { valid: false, error: 'Database error validating token' };
      }

      if (!data) {
        return { valid: false, error: 'Invalid or expired token' };
      }

      const expiresAt = new Date(data.expires_at);
      const now = new Date();

      if (expiresAt < now) {
        await this.markTokenAsUsed(token);
        return { valid: false, error: 'Token has expired' };
      }

      return { valid: true, record: data };
    } catch (error) {
      return { valid: false, error: 'Error validating token' };
    }
  }

  static async markTokenAsUsed(token: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('tokens')
        .update({ used_at: new Date().toISOString() })
        .eq('token', token);

      if (error) {
        return { success: false, error: 'Failed to update token' };
      }

      return { success: true };
    } catch (error) {
      return { success: false, error: 'An unexpected error occurred' };
    }
  }

  static async getActiveTokens(email: string, tokenType?: TokenType): Promise<TokenRecord[]> {
    try {
      let query = supabase
        .from('tokens')
        .select('*')
        .eq('email', email)
        .is('used_at', null)
        .gt('expires_at', new Date().toISOString());

      if (tokenType) {
        query = query.eq('token_type', tokenType);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) {
        return [];
      }

      return data || [];
    } catch (error) {
      return [];
    }
  }

  static async cleanupExpiredTokens(): Promise<{ deleted: number; error?: string }> {
    try {
      const { data, error } = await supabase.rpc('cleanup_expired_tokens');

      if (error) {
        return { deleted: 0, error: 'Failed to clean up tokens' };
      }

      return { deleted: data || 0 };
    } catch (error) {
      return { deleted: 0, error: 'An unexpected error occurred' };
    }
  }

  static async createEmailVerificationToken(email: string): Promise<{
    success: boolean;
    token?: string;
    error?: string;
  }> {
    return this.createToken({
      email,
      tokenType: 'email_verification',
      expiryHours: 1
    });
  }

  static async createPasswordResetToken(email: string): Promise<{
    success: boolean;
    token?: string;
    error?: string;
  }> {
    return this.createToken({
      email,
      tokenType: 'password_reset',
      expiryHours: 1
    });
  }
  

  static async verifyUserEmail(token: string): Promise<{
    success: boolean;
    email?: string;
    error?: string;
  }> {
    try {
      // 1. Validate the token using your existing generic validator
      const result = await this.validateToken(token, 'email_verification');

      if (!result.valid || !result.record) {
        return { success: false, error: result.error || 'Invalid token' };
      }

      const email = result.record.email;

      // 2. Call the RPC function to update auth.users
      const { data: rpcData, error: rpcError } = await supabase
        .rpc('verify_user_email', { target_email: email });

      if (rpcError) {
        console.error('RPC Error:', rpcError);
        return { success: false, error: 'Failed to activate account' };
      }

      if (!rpcData || !rpcData.success) {
        return { success: false, error: rpcData?.error || 'User not found' };
      }

      // 3. Mark token as used so it can't be used again
      await this.markTokenAsUsed(token);

      return { success: true, email };
    } catch (error) {
      console.error('Verification flow error:', error);
      return { success: false, error: 'An unexpected error occurred' };
    }
  }

  static async validateEmailVerificationToken(token: string): Promise<{
      valid: boolean;
      email?: string;
      record?: TokenRecord;
      error?: string;
    }> {
    const result = await this.validateToken(token, 'email_verification');
    
    if (result.valid && result.record) {
      return {
        valid: true,
        email: result.record.email,
        record: result.record
      };
    }
    
    return {
      valid: false,
      error: result.error
    };
  }

  static async validatePasswordResetToken(token: string): Promise<{
      valid: boolean;
      email?: string;
      record?: TokenRecord;
      error?: string;
    }> {
    const result = await this.validateToken(token, 'password_reset');
    
    if (result.valid && result.record) {
      return {
        valid: true,
        email: result.record.email,
        record: result.record
      };
    }
    
    return {
      valid: false,
      error: result.error
    };
  }
}